// Pure name-part helpers (Q2). Shared by the frontend (App.tsx) and the fast gate so the
// picker "wrong numbers" fix is locked by a deterministic test: numerology needs first/middle/
// last, so a picker that filled only the combined name (or only date + time) produced wrong
// numbers. These derive the parts and guarantee every name field fills.

export function combineName(first, middle, last) {
  return [first, middle, last].map((s) => (s || "").trim()).filter(Boolean).join(" ");
}

// Split a combined full name into first / middle / last: 1 token = first only; 2 = first + last;
// 3+ = first, everything-in-the-middle, last.
export function splitName(full) {
  const parts = (full || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", middleName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], middleName: "", lastName: "" };
  if (parts.length === 2) return { firstName: parts[0], middleName: "", lastName: parts[1] };
  return { firstName: parts[0], middleName: parts.slice(1, -1).join(" "), lastName: parts[parts.length - 1] };
}

// >>> ITEM 2 ADDITION (her order, 2026-08-25): A NAME THAT CANNOT CARRY NUMEROLOGY DOES NOT GET A
// NUMEROLOGY READING, AND THE FUNNEL IS WHY THIS EXISTS. <<<
//
// This file's own header has stated the law since Q2 — "numerology needs first/middle/last, so a
// picker that filled only the combined name produced WRONG NUMBERS" — and it was enforced at the
// PICKER alone. `/api/room/create` casts a hard-coded `["birth-chart", "numerology"]` on whatever
// name it is handed, and the /talk capture hands it a FIRST NAME ALONE (that form asks for a first
// name, an address, a birth date, a time and a place, and nothing else). So the law was written
// down, true, and unenforced at the one door a funnel arrival comes through — R121/R122's class,
// documentation read as installation.
//
// IT IS DERIVED FROM THE NAME AND DELIBERATELY NOT A FUNNEL FLAG. A flag is a fact a caller must
// remember, and the caller that forgets is the next door somebody adds (R41/R11/R31); the NAME is
// the thing numerology is actually cast on, so asking it fixes the CLASS rather than the instance
// and every existing door inherits the repair by EXISTING. It is also exactly her clause: when a
// funnel convert completes her room at membership onboarding she arrives with a full name, and the
// standard room creation runs numerology properly THEN, with no second code path.
//
// IT FAILS CLOSED, AND THE DIRECTION IS THE RULING (R19/R8-4). Casting on a partial name produces
// numbers that are simply FALSE and that a member reads as true — no error, no symptom, and nothing
// on the screen to say the reading is about a name she never gave. Withholding costs her a reading
// the completion path casts correctly later. Only one of those two is silent.
//
// IT DELEGATES TO `splitName` AND STATES NO SECOND OPINION about what a name's parts are (R28/R29):
// a private token count here and the picker's law would be two answers to one question, and the one
// that drifts decides whether a member's numbers are hers.
export function nameCarriesNumerology(full) {
  if (typeof full !== "string") return false;
  const { firstName, lastName } = splitName(full);
  return Boolean(firstName) && Boolean(lastName);
}

// The signup cast set, DERIVED. The birth chart needs a birth moment and never a surname, so it is
// unconditional; numerology is added only when the name can really carry it.
export function signupSetFor(full) {
  return nameCarriesNumerology(full) ? ["birth-chart", "numerology"] : ["birth-chart"];
}

// Ensure a chart carries first/middle/last: prefer explicit parts, else derive from the combined
// name. Every picker choice ("For me", a saved person, someone new) runs through this so ALL name
// fields fill every time and numerology never receives a partial name.
export function withNameParts(chart) {
  const first = (chart.firstName || "").trim();
  const middle = (chart.middleName || "").trim();
  const last = (chart.lastName || "").trim();
  const full = (chart.name || "").trim();

  // Nothing explicit: derive everything from the combined name.
  if (!first && !middle && !last) return { ...chart, ...splitName(full) };

  // P1: SOME parts explicit. This used to return here immediately, which is how the owner's own
  // middle name went missing on "For me": her profile is a legacy row (name only), and getProfile
  // filled first and last from the name but left middle empty, so "has some parts" was true and
  // the middle could never be recovered. Numerology needs all three, so a MISSING part is now
  // derived from the combined name instead of being kept empty.
  if (full && (!first || !middle || !last)) {
    const derived = splitName(full);
    let derivedMiddle = derived.middleName;
    // When first and last are explicit and the full name reads "first ... last", the middle is
    // exactly what sits between them. Doing it this way (rather than trusting splitName's
    // tokens) is what keeps a multi-word first name intact: "Mary Jo Smith" with firstName
    // "Mary Jo" correctly yields NO middle name, instead of inventing "Jo".
    if (first && last && full.startsWith(first) && full.endsWith(last)) {
      derivedMiddle = full.slice(first.length, full.length - last.length).trim();
    }
    return {
      ...chart,
      firstName: first || derived.firstName,
      middleName: middle || derivedMiddle,
      lastName: last || derived.lastName,
      name: full,
    };
  }

  return { ...chart, name: full || combineName(first, middle, last) };
}
